import React, { useState, useEffect } from 'react';
import { ChevronRight, Calendar, User, ExternalLink, FileText, TrendingUp, Globe, Shield, Zap, Brain } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// Assuming BlogSection is in the same directory or correctly aliased
import BlogSection from '../BlogSection/BlogSection';

const MediaAndResources = () => {
  const [news, setNews] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('blog');
  const navigate = useNavigate();

  // API Base URL from environment
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  // Fetch News from API
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/news`);
        if (!response.ok) throw new Error('Failed to fetch news');
        const newsData = await response.json();
        setNews(newsData);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Failed to load news');
      }
    };

    const fetchReports = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/report`);
        if (!response.ok) throw new Error('Failed to fetch reports');
        const reportsData = await response.json();
        setReports(reportsData);
      } catch (err) {
        console.error('Error fetching reports:', err);
        setError('Failed to load reports');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
    fetchReports();
  }, [API_BASE_URL]);

  // Handle report navigation
  const handleReportClick = (reportId) => {
    navigate('/report', { state: { reportId } });
  };

  // Category icons mapping
  const getCategoryIcon = (category) => {
    const iconMap = {
      'Quantum Security': <Shield className="w-5 h-5" />,
      'IoT Security': <Zap className="w-5 h-5" />,
      'Cloud Security': <Globe className="w-5 h-5" />,
      'AI Security': <Brain className="w-5 h-5" />,
      'Threat Intelligence': <TrendingUp className="w-5 h-5" />,
      'Research': <FileText className="w-5 h-5" />
    };
    return iconMap[category] || <FileText className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="loading-spinner">
          <div className="spinner-ring"></div>
          <p className="mt-4 text-[#1e40af] font-semibold">Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Geometric Shapes */}
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="grid-pattern"></div>
        </div>

        {/* Gradient Orbs */}
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6 shadow-lg">
            <Globe className="w-4 h-4 mr-2" />
            GLOCYBS Media Center
          </div>
          
          <h1 className="text-5xl lg:text-7xl font-black mb-6 leading-tight">
            <span className="bg-gradient-to-r from-[#1e40af] via-[#0ea5e9] to-[#06b6d4] bg-clip-text text-transparent animate-gradient-text">
              Media &
            </span>
            <br />
            <span className="text-[#1f2937]">Resources</span>
          </h1>
          
          <p className="text-xl text-[#6b7280] mb-8 max-w-3xl mx-auto leading-relaxed">
            Discover cutting-edge insights from GLOCYBS experts on quantum security, AI-powered threat detection, 
            and the evolving cybersecurity landscape with BIQUA, G-FLEA, and QUBISA technologies.
          </p>

          {/* Tab Navigation */}
          <div className="inline-flex bg-[#f8fafc] rounded-2xl p-2 shadow-lg border border-[#e2e8f0] mb-12">
            <button
              onClick={() => setActiveTab('blog')}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === 'blog'
                  ? 'bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white shadow-lg transform scale-105'
                  : 'text-[#64748b] hover:text-[#1e40af] hover:bg-white'
              }`}
            >
              Blog Posts
            </button>
            <button
              onClick={() => setActiveTab('news')}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === 'news'
                  ? 'bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white shadow-lg transform scale-105'
                  : 'text-[#64748b] hover:text-[#1e40af] hover:bg-white'
              }`}
            >
              Latest News
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === 'reports'
                  ? 'bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white shadow-lg transform scale-105'
                  : 'text-[#64748b] hover:text-[#1e40af] hover:bg-white'
              }`}
            >
              Research Reports
            </button>
          </div>
        </div>

        {/* Blog Section */}
        {activeTab === 'blog' && (
          <div className="animate-fade-in-up">
            <BlogSection />
          </div>
        )}

        {/* Error Handling */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 text-center">
            <p className="text-red-600 font-semibold">{error}</p>
          </div>
        )}

        {/* News Section */}
        {activeTab === 'news' && (
          <div className="animate-fade-in-up">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {news.length > 0 ? news.map((item, index) => (
                <div
                  key={item._id || item.id}
                  className="group bg-white rounded-3xl border border-[#e2e8f0] overflow-hidden hover:border-[#0ea5e9] transition-all duration-500 transform hover:scale-105 hover:shadow-2xl cursor-pointer news-card"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] rounded-xl text-white">
                        {getCategoryIcon(item.category)}
                      </div>
                      <div>
                        <span className="text-xs text-[#64748b] font-medium uppercase tracking-wide">
                          {item.category || 'News'}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-[#94a3b8]">
                          <Calendar className="w-3 h-3" />
                          {new Date(item.date || item.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-[#1f2937] mb-4 leading-tight group-hover:text-[#0ea5e9] transition-colors duration-300">
                      {item.title}
                    </h3>
                    
                    <p className="text-[#6b7280] text-sm mb-6 line-clamp-3 leading-relaxed">
                      {item.description || item.content}
                    </p>
                    
                    <a
                      href={item.link || '#'}
                      className="inline-flex items-center gap-2 text-[#0ea5e9] hover:text-[#1e40af] transition-all duration-300 font-semibold group/btn"
                    >
                      <span>Read More</span>
                      <ChevronRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                    </a>
                  </div>
                  
                  <div className="h-1 bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </div>
              )) : (
                <div className="col-span-full text-center py-16">
                  <div className="text-[#94a3b8] text-lg">No news articles available at the moment.</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reports Section */}
        {activeTab === 'reports' && (
          <div className="animate-fade-in-up">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-16">
              {reports.length > 0 ? reports.map((report, index) => (
                <div
                  key={report._id || report.id}
                  className="group bg-gradient-to-br from-white to-[#f8fafc] rounded-3xl border border-[#e2e8f0] overflow-hidden hover:border-[#0ea5e9] transition-all duration-300 hover:shadow-2xl report-card w-full h-full"
                  style={{ animationDelay: `${index * 200}ms` }}
                >
                  <div className="p-6 md:p-8 h-full flex flex-col">
                    <div className="flex items-start justify-between mb-6">
                      <div className="p-3 bg-gradient-to-r from-[#06b6d4] to-[#0ea5e9] rounded-2xl text-white flex-shrink-0">
                        <FileText className="w-5 h-5 md:w-6 md:h-6" />
                      </div>
                      <span className="bg-[#eff6ff] text-[#1e40af] px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ml-2">
                        RESEARCH
                      </span>
                    </div>
                    
                    <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-[#1f2937] mb-4 leading-tight group-hover:text-[#0ea5e9] transition-colors duration-300 flex-grow-0">
                      {report.title}
                    </h3>
                    
                    {/* The primary change is here: remove line-clamp if it's causing issues, or ensure the parent can accommodate */}
                    <p className="text-[#6b7280] mb-6 leading-relaxed text-sm md:text-base flex-grow overflow-hidden">
                      {report.description || report.summary || report.content}
                    </p>
                    
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-auto">
                      <div className="flex items-center gap-2 text-sm text-[#64748b]">
                        <User className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{report.author || 'GLOCYBS Research Team'}</span>
                      </div>
                      
                      <div 
                        className="inline-flex items-center cursor-pointer flex-shrink-0"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleReportClick(report._id || report.id);
                        }}
                      >
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white px-4 md:px-6 py-2 md:py-3 rounded-xl hover:shadow-lg transition-all duration-300 font-semibold hover:from-[#0284c7] hover:to-[#2563eb] focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:ring-offset-2 text-sm md:text-base"
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span>View Report</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="col-span-full text-center py-16">
                  {loading ? (
                    <div className="loading-spinner">
                      <div className="spinner-ring"></div>
                      <p className="mt-4 text-[#1e40af] font-semibold">Loading reports...</p>
                    </div>
                  ) : (
                    <div className="text-[#94a3b8] text-lg">No research reports available at the moment.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center bg-gradient-to-r from-[#1e40af] to-[#0ea5e9] rounded-3xl p-12 text-white animate-fade-in-up">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Stay Updated with GLOCYBS
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Subscribe to our newsletter for the latest insights on quantum security and AI-powered cybersecurity.
          </p>
          <button className="bg-white text-[#1e40af] px-8 py-4 rounded-2xl font-bold hover:shadow-lg transition-all duration-300 transform hover:scale-105">
            Subscribe Now
          </button>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes fade-in-up {
          from { 
            opacity: 0; 
            transform: translateY(40px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }

        @keyframes float-reverse {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(20px) rotate(-180deg); }
        }

        @keyframes pulse-glow {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(14, 165, 233, 0.3),
                        0 0 40px rgba(14, 165, 233, 0.1); 
          }
          50% { 
            box-shadow: 0 0 40px rgba(14, 165, 233, 0.5),
                        0 0 80px rgba(14, 165, 233, 0.2); 
          }
        }

        @keyframes gradient-text {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animate-gradient-text {
          background-size: 200% 200%;
          animation: gradient-text 3s ease-in-out infinite;
        }

        .floating-shape {
          position: absolute;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(59, 130, 246, 0.1));
          animation: float 6s ease-in-out infinite;
        }

        .shape-1 {
          width: 100px;
          height: 100px;
          top: 10%;
          left: 10%;
          animation-delay: 0s;
        }

        .shape-2 {
          width: 150px;
          height: 150px;
          top: 60%;
          right: 10%;
          animation: float-reverse 8s ease-in-out infinite;
          animation-delay: 2s;
        }

        .shape-3 {
          width: 80px;
          height: 80px;
          top: 30%;
          right: 30%;
          animation-delay: 4s;
        }

        .shape-4 {
          width: 120px;
          height: 120px;
          bottom: 20%;
          left: 20%;
          animation: float-reverse 7s ease-in-out infinite;
          animation-delay: 1s;
        }

        .grid-pattern {
          width: 100%;
          height: 100%;
          background-image: 
            linear-gradient(rgba(14, 165, 233, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: float 20s linear infinite;
        }

        .gradient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(40px);
          animation: float 10s ease-in-out infinite;
        }

        .orb-1 {
          width: 300px;
          height: 300px;
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.15), rgba(6, 182, 212, 0.15));
          top: 20%;
          left: -10%;
          animation-delay: 0s;
        }

        .orb-2 {
          width: 250px;
          height: 250px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(147, 51, 234, 0.15));
          bottom: 20%;
          right: -10%;
          animation-delay: 5s;
        }

        .orb-3 {
          width: 200px;
          height: 200px;
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(14, 165, 233, 0.15));
          top: 50%;
          left: 50%;
          animation-delay: 3s;
        }

        .news-card {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
          position: relative;
          z-index: 1;
        }

        .report-card {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
          position: relative;
          z-index: 1;
        }

        .news-card:hover {
          animation: pulse-glow 2s ease-in-out infinite;
          transform: scale(1.05);
        }

        .report-card:hover {
          box-shadow: 0 20px 25px -5px rgba(14, 165, 233, 0.1), 0 10px 10px -5px rgba(14, 165, 233, 0.04);
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-box-orient: vertical;
          overflow: hidden;
          -webkit-line-clamp: 3;
        }

        /* Adjusted for report cards */
        .report-card .line-clamp-4 {
          /* Remove if it was causing issues. If you need it, ensure it's not overriding 'flex-grow' inappropriately. */
          /* Forcing content to fit without clamping might be better for full view */
          /* If you must clamp, ensure the overall flex container can handle the space */
          display: -webkit-box;
          -webkit-box-orient: vertical;
          overflow: hidden;
          -webkit-line-clamp: 4; /* Keep if you still want clamping after ensuring full card display */
        }

        .loading-spinner {
          text-align: center;
        }

        .spinner-ring {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(14, 165, 233, 0.2);
          border-top: 4px solid #0ea5e9;
          border-radius: 50%;
          animation: spin-slow 1s linear infinite;
          margin: 0 auto;
        }
      `}</style>
    </div>
  );
};

export default MediaAndResources;